import { getOfferText } from './offer-text';

// Add a global flag to track if we've initialized for a form
const initializedForms = new Set();

export const useFormAbandonment = (form) => {
    // Check if abandonment is disabled for this form
    if (form.hasAttribute('data-disable-abandonment')) {
        // console.log('Form abandonment disabled for form:', form.id);
        return;
    }

    // Check if we've already initialized for this form
    if (initializedForms.has(form.id)) {
        // console.log('Form abandonment already initialized for form:', form.id);
        return;
    }
    initializedForms.add(form.id);

    // console.log('Form abandonment tracking initialized for form:', form);
    // console.log('Form attributes:', {
    //     'form-id': form.id,
    //     'all-attributes': Array.from(form.attributes).map(attr => ({
    //         name: attr.name,
    //         value: attr.value
    //     }))
    // });

    // Get the offer text from our centralized function
    const offerText = getOfferText(form);
    // console.log('Using offer text:', offerText);

    let inactivityTimer;
    let hasSeenPopup = false;
    let lastActivityTime = Date.now();
    const INACTIVITY_THRESHOLD = 6000; // 6 second timeout
    let popup = null;

    // Check if any field has been filled
    const hasFilledField = () => {
        // Get all form fields, excluding hidden fields and buttons
        const fields = Array.from(form.elements).filter(field => {
            return field.type !== 'hidden' &&
                field.type !== 'submit' &&
                field.type !== 'button';
        });

        // Only check the first field
        const firstField = fields[0];
        if (!firstField) return false;

        // Handle different field types
        if (firstField.type === 'radio' || firstField.type === 'checkbox') {
            return firstField.checked;
        }

        // For other fields, check if they have a value
        const hasValue = firstField.value && firstField.value.trim() !== '';
        // console.log('First field filled:', hasValue);
        return hasValue;
    };

    // Track any form interaction
    const trackInteraction = () => {
        // console.log('Form interaction detected');
        lastActivityTime = Date.now();

        // Only start timer if we haven't seen the popup yet
        if (!hasSeenPopup) {
            // console.log('Starting inactivity timer');
            startInactivityTimer();
        } else {
            // If we've seen the popup, clear any existing timer
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
                inactivityTimer = null;
            }
        }
    };

    // Start inactivity timer
    const startInactivityTimer = () => {
        // console.log('Starting inactivity timer');
        if (inactivityTimer) {
            clearTimeout(inactivityTimer);
        }
        inactivityTimer = setTimeout(() => {
            const timeSinceLastActivity = Date.now() - lastActivityTime;
            // console.log('Timer fired. Time since last activity:', timeSinceLastActivity);
            // console.log('Has seen popup:', hasSeenPopup);
            // console.log('Has filled field:', hasFilledField());

            // Double check hasSeenPopup here to be extra safe
            if (timeSinceLastActivity >= INACTIVITY_THRESHOLD && !hasSeenPopup && hasFilledField()) {
                // console.log('Conditions met, showing popup');
                showAbandonmentPopup();
            } else {
                // console.log('Conditions not met:', {
                //     timeSinceLastActivity,
                //     hasSeenPopup,
                //     hasFilledField: hasFilledField()
                // });
            }
        }, INACTIVITY_THRESHOLD);
    };

    const showAbandonmentPopup = () => {
        // Double check hasSeenPopup here to be extra safe
        if (hasSeenPopup) {
            // console.log('Popup already seen, not showing again');
            return;
        }

        // console.log('Creating and showing abandonment popup');
        // Check if popup already exists
        const existingPopup = document.querySelector('.FormAbandonmentModal');
        if (existingPopup) {
            // console.log('Popup already exists, not creating another one');
            return;
        }

        // Clear any existing timer
        if (inactivityTimer) {
            clearTimeout(inactivityTimer);
            inactivityTimer = null;
        }

        popup = document.createElement('div');
        popup.className = 'FormAbandonmentModal FormAbandonmentModal--fade is-open';
        popup.setAttribute('aria-hidden', 'false');
        popup.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999; display: flex; align-items: center; justify-content: center;';
        popup.innerHTML = `
            <div class="FormAbandonmentModal-overlay" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.5);">
                <div class="FormAbandonmentModal-container" style="position: relative; background: white; padding: 2rem; border-radius: 8px; max-width: 500px; width: 90%; margin: 0 auto;">
                    <button
                        class="FormAbandonmentModal-close"
                        aria-label="Close modal"
                        data-modal-close
                        style="position: absolute; top: 1rem; right: 1rem; background: none; border: none; cursor: pointer;"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <div class="FormAbandonmentModal-content">
                        <h3 class="Heading-4">Still thinking it over?</h3>
                        <p>${offerText ? `No rush—we're here when you're ready. For a limited time, we're offering ${offerText}. <b>Finish booking your appointment to lock in your savings.</b>` : 'No rush—we\'re here when you\'re ready. If you have any questions, we\'d be happy to help:<br /><a href="tel:877-846-4085" style="color: #2e6cb1;">877-846-4085</a>'}</p>
                        <div class="FormAbandonmentModal-actions" style="margin-top: 1.5rem;">
                            <button class="Button Button--YellowSolid" style="width: 100%;">${offerText ? 'Claim My Savings' : 'Continue'}</button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add event listeners
        popup.querySelector('.Button--YellowSolid').addEventListener('click', () => {
            // console.log('Continue button clicked');
            hasSeenPopup = true; // Set this before removing the popup
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
                inactivityTimer = null;
            }
            popup.setAttribute('aria-hidden', 'true');
            setTimeout(() => popup.remove(), 300);

            // Scroll to form
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Find first empty field and focus it
            const firstEmptyField = Array.from(form.elements).find(field => {
                // Skip hidden fields and buttons
                if (field.type === 'hidden' || field.type === 'submit' || field.type === 'button') {
                    return false;
                }
                // Skip checkboxes and radio buttons that are checked
                if ((field.type === 'checkbox' || field.type === 'radio') && field.checked) {
                    return false;
                }
                // For other fields, check if they have a value
                return !field.value || field.value.trim() === '';
            });

            if (firstEmptyField) {
                firstEmptyField.focus();
            }
        });

        popup.querySelector('.FormAbandonmentModal-close').addEventListener('click', () => {
            // console.log('Close button clicked');
            hasSeenPopup = true; // Set this before removing the popup
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
                inactivityTimer = null;
            }
            popup.setAttribute('aria-hidden', 'true');
            setTimeout(() => popup.remove(), 300);
        });

        // Add popup to DOM
        document.body.appendChild(popup);
        // console.log('Popup added to DOM');

        // Verify popup is visible
        const addedPopup = document.querySelector('.FormAbandonmentModal');
        if (addedPopup) {
            // console.log('Popup successfully added to DOM');
            const style = window.getComputedStyle(addedPopup);
            // console.log('Popup display style:', style.display);
            // console.log('Popup visibility:', style.visibility);
            // console.log('Popup opacity:', style.opacity);
        } else {
            // console.error('Failed to add popup to DOM');
        }
    };

    // Add event listeners to form fields
    // console.log('Adding event listeners to form fields');
    form.addEventListener('input', trackInteraction);
    form.addEventListener('change', trackInteraction);
    form.addEventListener('focus', trackInteraction);

    // Start the initial timer
    startInactivityTimer();

    // Clean up on form submit
    form.addEventListener('submit', () => {
        // console.log('Form submitted, cleaning up timers');
        if (inactivityTimer) clearTimeout(inactivityTimer);
    });
};
